import React, { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import {
  ServiceOrIssueStatus,
  TrackerDataType,
  TABLE_REFETCH_INTERVAL_MILISEC,
} from '../../shared/constants/constants';
import dayjs from '../../utils/dayjs';
import { useEngagementLogHandler } from '../../shared/eventHandler/EngagementLogEventHandler';
import { useMediaInteractionHandler } from '../../shared/eventHandler/MediaInteractionEventHandler';
import { useServiceLogHandler } from '../../shared/eventHandler/ServiceLogEventHandler';
import { useContentLogHandler } from '../../shared/eventHandler/ContentLogEventHandler';
import EventModal from './EventModal';
import { useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { DropdownTable, FlatContentLog, FlatEngagement, FlatIssue, FlatService } from '../../shared/types';
import { useRecentCreations } from '../../shared/hooks/useRecentCreations';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import CalendarTopBar from './CalendarTopBar';
import CalendarCategoryBar, { CalendarTopBarView } from './CalendarCategoryBar';
import CalendarMonthGrid from './CalendarMonthGrid';
import QueryAdder from '../QueryAdder/QueryAdder';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import CalendarSelectedDayPanel from './CalendarSelectedDayPanel';
import { ALL_FIELDS_SEARCH_CATEGORY, calendarSearchCategoryOptions, categoryOptions } from './calendarConfig';
import CalendarNewEntryMenu from './CalendarNewEntryMenu';
import { getCurrMonth, getNextMonth, getPrevMonth } from '../../shared/functions/dateProcessor';
import {
  getCurrentUserInProgressFilters,
  getTrackerFilterQueryFromFilters,
  isCurrentUserInProgressFilterActive,
  removeCurrentUserInProgressFilters,
} from '../TrackerFilter/trackerFilterHelpers';
import { getCalendarFilteredEngagements, getMonthlyEngagements } from '../../shared/requests/engagementsRequest';
import { getCalendarFilteredInteractions, getMonthlyInteractions } from '../../shared/requests/interactionsRequests';
import { getCalendarFilteredServices, getMonthlyServices } from '../../shared/requests/serviceNetworkRequests';
import { getCalendarFilteredContentLogs, getMonthlyContentLogs } from '../../shared/requests/contentLogsRequest';
import { flattenMonthlyQueryData, formatCalendarEvents, groupEventsByDay } from './calendarEventUtils';
import { CalendarDateBasis, CalendarEvent } from './types';
import { useCalendarSearchAutocomplete } from './useCalendarSearchAutocomplete';
import { StoreState, UserState } from '../../redux-types';
import { getCalendarUiPalette } from './calendarPalette';

interface CalendarViewProps {
  dropdownTable: DropdownTable | null;
  hasEngagementAccess: boolean;
  hasContentLogAccess: boolean;
}

const CALENDAR_MONTH_STALE_TIME_MS = 5 * 60 * 1000;

function getCalendarSourceData<T extends FlatIssue | FlatService | FlatEngagement | FlatContentLog>({
  isSelected,
  filtersOn,
  filteredData,
  hasLoadedWindow,
  monthlyData,
}: {
  isSelected: boolean;
  filtersOn: boolean;
  filteredData: T[] | undefined;
  hasLoadedWindow: boolean;
  monthlyData: T[];
}) {
  if (!isSelected) {
    return [];
  }

  if (filtersOn) {
    return filteredData ?? [];
  }

  if (!hasLoadedWindow) {
    return [];
  }

  return monthlyData;
}

function sortCalendarEventsById<T extends CalendarEvent>(events: T[]) {
  return [...events].sort((left, right) => {
    if (left.id === right.id) {
      return left.logtype.localeCompare(right.logtype);
    }

    return String(left.id).localeCompare(String(right.id), undefined, { numeric: true });
  });
}

function getActionView({
  hasServiceSelected,
  hasMediaSelected,
  hasEngagementSelected,
  hasContentSelected,
}: {
  hasServiceSelected: boolean;
  hasMediaSelected: boolean;
  hasEngagementSelected: boolean;
  hasContentSelected: boolean;
}) {
  if (hasServiceSelected) return TrackerDataType.SERVICE;
  if (hasMediaSelected) return TrackerDataType.MEDIA_INTERACTION;
  if (hasEngagementSelected) return TrackerDataType.ENGAGEMENT;
  if (hasContentSelected) return TrackerDataType.CONTENT_LOG;
  return null;
}

function isMonthlyQueryEnabled(isSelected: boolean, hasActiveFilters: boolean) {
  return isSelected && !hasActiveFilters;
}

function getMonthlyRefetchInterval(enabled: boolean) {
  return enabled ? TABLE_REFETCH_INTERVAL_MILISEC : false;
}

function getSafeInitialMonth(storedMonth: string | null) {
  if (!storedMonth) {
    return getCurrMonth();
  }

  const [yearToken, monthToken] = storedMonth.split('-');
  const year = Number(yearToken);
  const month = Number(monthToken);
  const isValidStoredMonth =
    /^\d{4}-\d{2}$/.test(storedMonth) &&
    Number.isInteger(year) &&
    year >= 1970 &&
    year <= 9999 &&
    Number.isInteger(month) &&
    month >= 1 &&
    month <= 12;

  return isValidStoredMonth ? storedMonth : getCurrMonth();
}

interface MonthlyPrefetchConfig {
  enabled: boolean;
  queryKeyPrefix: string;
  queryFn: (month: string) => Promise<unknown>;
}

function createMonthlyPrefetch({
  queryClient,
  dateBasis,
  month,
  config,
}: {
  queryClient: QueryClient;
  dateBasis: CalendarDateBasis;
  month: string;
  config: MonthlyPrefetchConfig;
}) {
  if (!config.enabled) {
    return null;
  }

  return queryClient.prefetchQuery({
    queryKey: [config.queryKeyPrefix, month, dateBasis],
    queryFn: () => config.queryFn(month),
    staleTime: CALENDAR_MONTH_STALE_TIME_MS,
  });
}

function getMonthlyPrefetches({
  queryClient,
  dateBasis,
  months,
  configs,
}: {
  queryClient: QueryClient;
  dateBasis: CalendarDateBasis;
  months: string[];
  configs: MonthlyPrefetchConfig[];
}) {
  const prefetches: Promise<void>[] = [];

  for (const month of months) {
    for (const config of configs) {
      const prefetch = createMonthlyPrefetch({ queryClient, dateBasis, month, config });
      if (prefetch) {
        prefetches.push(prefetch);
      }
    }
  }

  return prefetches;
}

function isFilteredQueryEnabled(filtersOn: boolean, queryString: string, isSelected: boolean) {
  return filtersOn && Boolean(queryString) && isSelected;
}

function isFilteredQueryLoading(
  isSelected: boolean,
  filtersOn: boolean,
  query: { isLoading: boolean; isFetching: boolean }
) {
  return isSelected && filtersOn && (query.isLoading || query.isFetching);
}

function isMonthlyQueryLoading(
  query: { isLoading: boolean; isFetching: boolean; isSuccess: boolean } | undefined,
  isSelected: boolean,
  filtersOn: boolean
) {
  if (!query || !isSelected || filtersOn) {
    return false;
  }

  const hasPendingQuery = query.isLoading || query.isFetching;
  const hasUnloadedMonth = !query.isSuccess;

  return hasPendingQuery && hasUnloadedMonth;
}

function getFilterConfig(
  actionView: CalendarTopBarView | null,
  dropdownTable: DropdownTable,
  getEventHandler: (view: CalendarTopBarView) => ReturnType<typeof useServiceLogHandler>,
  setFiltersOn: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!actionView) {
    return null;
  }

  return {
    dropdownTable,
    eventHandler: getEventHandler(actionView),
    setFiltersOn,
    disableFilter: false,
    filterTooltipTitle: '',
  };
}

function CalendarView({ dropdownTable, hasEngagementAccess, hasContentLogAccess }: Readonly<CalendarViewProps>) {
  const queryClient = useQueryClient();
  const recentCreations = useRecentCreations();
  const currentUser = useSelector<StoreState, UserState>((state) => state.user);
  const theme = useTheme();
  const calendarUiPalette = getCalendarUiPalette(theme);

  const filters = useFilters();
  const filtersDispatch = useFiltersDispatch();
  const initialMonth = getSafeInitialMonth(sessionStorage.getItem('currMonth'));
  const hasDropdownTable = Boolean(dropdownTable);
  const availableCategoryOptions = useMemo(
    () =>
      categoryOptions.filter(
        (option) =>
          (option.key !== TrackerDataType.ENGAGEMENT || hasEngagementAccess) &&
          (option.key !== TrackerDataType.CONTENT_LOG || hasContentLogAccess)
      ),
    [hasContentLogAccess, hasEngagementAccess]
  );
  const [selectedViews, setSelectedViews] = useState<CalendarTopBarView[]>(() =>
    availableCategoryOptions.map((option) => option.key)
  );
  const [currMonth, setCurrMonth] = useState<string>(initialMonth);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const monthStart = dayjs(`${initialMonth}-01`).startOf('month');
    return dayjs().isSame(monthStart, 'month') ? dayjs().startOf('day').toDate() : monthStart.toDate();
  });
  const [newEntryMenuAnchor, setNewEntryMenuAnchor] = useState<null | HTMLElement>(null);
  const [newEntryView, setNewEntryView] = useState<CalendarTopBarView | null>(null);
  const [newEntryInitialDate, setNewEntryInitialDate] = useState<Date>(() => dayjs().startOf('day').toDate());
  const [filtersOn, setFiltersOn] = useState(false);
  const [dateBasis, setDateBasis] = useState<CalendarDateBasis>('dueDate');
  const [searchCategory, setSearchCategory] = useState<string>(ALL_FIELDS_SEARCH_CATEGORY);
  const [searchInputValue, setSearchInputValue] = useState('');
  const selectedDayPanelRef = useRef<HTMLDivElement | null>(null);

  const selectedViewSet = useMemo(() => new Set(selectedViews), [selectedViews]);
  const hasServiceSelected = selectedViewSet.has(TrackerDataType.SERVICE);
  const hasMediaSelected = selectedViewSet.has(TrackerDataType.MEDIA_INTERACTION);
  const hasEngagementSelected = hasEngagementAccess && selectedViewSet.has(TrackerDataType.ENGAGEMENT);
  const hasContentSelected = hasContentLogAccess && selectedViewSet.has(TrackerDataType.CONTENT_LOG);
  const actionView = useMemo<CalendarTopBarView | null>(
    () =>
      getActionView({
        hasServiceSelected,
        hasMediaSelected,
        hasEngagementSelected,
        hasContentSelected,
      }),
    [hasContentSelected, hasEngagementSelected, hasMediaSelected, hasServiceSelected]
  );
  const addableCategories = useMemo(
    () =>
      availableCategoryOptions.filter(
        ({ key }) =>
          selectedViews.includes(key) &&
          (key === TrackerDataType.MEDIA_INTERACTION ||
            key === TrackerDataType.SERVICE ||
            key === TrackerDataType.ENGAGEMENT ||
            key === TrackerDataType.CONTENT_LOG)
      ),
    [availableCategoryOptions, selectedViews]
  );
  const isEventModalOpen = selectedEvent !== null;
  const hasCalendarActions = actionView !== null;
  const isCalendarEvent = dateBasis === 'dueDate';
  const hasActiveFilters = filtersOn && Object.keys(filters).length > 0;

  const calendarMonths = useMemo(() => [getPrevMonth(currMonth), currMonth, getNextMonth(currMonth)], [currMonth]);
  const adjacentCalendarMonths = useMemo(() => [calendarMonths[0], calendarMonths[2]], [calendarMonths]);
  const currentMonthQueryIndex = 1;
  const calendarWindowStart = useMemo(() => dayjs(`${calendarMonths[0]}-01`).startOf('month'), [calendarMonths]);
  const calendarWindowEnd = useMemo(() => dayjs(`${calendarMonths[2]}-01`).endOf('month'), [calendarMonths]);

  useEffect(() => {
    sessionStorage.setItem('currMonth', currMonth);
  }, [currMonth]);

  useEffect(() => {
    if (!hasEngagementAccess) {
      setSelectedViews((currentViews) => currentViews.filter((view) => view !== TrackerDataType.ENGAGEMENT));
    }

    if (hasContentLogAccess) {
      return;
    }

    setSelectedViews((currentViews) => currentViews.filter((view) => view !== TrackerDataType.CONTENT_LOG));
  }, [hasContentLogAccess, hasEngagementAccess]);

  const invalidateCalendarMonthWindow = useCallback(
    async (queryPrefix: 'media' | 'services' | 'engagements' | 'contentLogs') => {
      await Promise.all(
        calendarMonths.map((month) => queryClient.invalidateQueries({ queryKey: [queryPrefix, month] }))
      );
    },
    [calendarMonths, queryClient]
  );

  const mediaInteractionEventHandler = useMediaInteractionHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await invalidateCalendarMonthWindow('media');
    },
  });

  const serviceLogEventHandler = useServiceLogHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await invalidateCalendarMonthWindow('services');
    },
  });
  const engagementLogEventHandler = useEngagementLogHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await invalidateCalendarMonthWindow('engagements');
    },
  });
  const contentLogEventHandler = useContentLogHandler({
    recentCreations,
    onSuccessCallback: async () => {
      await invalidateCalendarMonthWindow('contentLogs');
    },
  });

  const getEventHandler = (view: CalendarTopBarView) => {
    switch (view) {
      case TrackerDataType.MEDIA_INTERACTION:
        return mediaInteractionEventHandler;
      case TrackerDataType.ENGAGEMENT:
        return engagementLogEventHandler;
      case TrackerDataType.CONTENT_LOG:
        return contentLogEventHandler;
      default:
        return serviceLogEventHandler;
    }
  };

  const handleToggleView = (view: CalendarTopBarView) => {
    setSelectedViews((currentViews) => {
      if (currentViews.includes(view)) {
        if (currentViews.length === 1) {
          return currentViews;
        }

        return currentViews.filter((currentView) => currentView !== view);
      }

      return [...currentViews, view];
    });
  };

  const handleMonthChange = (newMonth: string) => {
    setCurrMonth(newMonth);
    setSelectedDate((currentSelectedDate) => {
      const nextMonth = dayjs(`${newMonth}-01`).startOf('month');
      const currentSelection = dayjs(currentSelectedDate);

      if (currentSelection.isSame(nextMonth, 'month')) {
        return currentSelectedDate;
      }

      if (dayjs().isSame(nextMonth, 'month')) {
        return dayjs().startOf('day').toDate();
      }

      return nextMonth.startOf('month').toDate();
    });
  };

  const openEventModal = (event: CalendarEvent) => {
    setSelectedDate(dayjs(event.calendarDate).startOf('day').toDate());
    setSelectedEvent(event);
  };

  const openSearchResultModal = (event: CalendarEvent) => {
    setSelectedEvent(event);
  };

  const closeEventModal = () => {
    setSelectedEvent(null);
  };

  const handleNewEntryClick = (event: MouseEvent<HTMLButtonElement>, initialEntryDate: Date) => {
    if (!hasCalendarActions) {
      return;
    }

    setNewEntryInitialDate(dayjs(initialEntryDate).startOf('day').toDate());

    if (addableCategories.length > 1) {
      setNewEntryMenuAnchor(event.currentTarget);
      return;
    }

    if (actionView) {
      setNewEntryView(actionView);
    }
  };

  const openNewEntryDrawer = (view: CalendarTopBarView) => {
    setNewEntryMenuAnchor(null);
    setNewEntryView(view);
  };

  const closeNewEntryMenu = () => {
    setNewEntryMenuAnchor(null);
  };

  const closeNewEntryDrawer = () => {
    setNewEntryView(null);
  };

  const timezone = dayjs.tz.guess();
  const isServiceMonthlyQueryEnabled = isMonthlyQueryEnabled(hasServiceSelected, hasActiveFilters);
  const isMediaMonthlyQueryEnabled = isMonthlyQueryEnabled(hasMediaSelected, hasActiveFilters);
  const isEngagementMonthlyQueryEnabled = isMonthlyQueryEnabled(hasEngagementSelected, hasActiveFilters);
  const isContentMonthlyQueryEnabled = isMonthlyQueryEnabled(hasContentSelected, hasActiveFilters);
  const serviceMonthlyQueries = useQueries({
    queries: calendarMonths.map((month) => ({
      queryKey: ['services', month, dateBasis],
      queryFn: () => getMonthlyServices(month, timezone, isCalendarEvent, true),
      enabled: hasDropdownTable && isServiceMonthlyQueryEnabled && month === currMonth,
      refetchInterval: getMonthlyRefetchInterval(isServiceMonthlyQueryEnabled),
      staleTime: CALENDAR_MONTH_STALE_TIME_MS,
    })),
  });
  const mediaMonthlyQueries = useQueries({
    queries: calendarMonths.map((month) => ({
      queryKey: ['media', month, dateBasis],
      queryFn: () => getMonthlyInteractions(month, timezone, isCalendarEvent, true),
      enabled: hasDropdownTable && isMediaMonthlyQueryEnabled && month === currMonth,
      refetchInterval: getMonthlyRefetchInterval(isMediaMonthlyQueryEnabled),
      staleTime: CALENDAR_MONTH_STALE_TIME_MS,
    })),
  });
  const engagementMonthlyQueries = useQueries({
    queries: calendarMonths.map((month) => ({
      queryKey: ['engagements', month, dateBasis],
      queryFn: () => getMonthlyEngagements(month, timezone, isCalendarEvent, true),
      enabled: hasDropdownTable && isEngagementMonthlyQueryEnabled && month === currMonth,
      refetchInterval: getMonthlyRefetchInterval(isEngagementMonthlyQueryEnabled),
      staleTime: CALENDAR_MONTH_STALE_TIME_MS,
    })),
  });
  const contentMonthlyQueries = useQueries({
    queries: calendarMonths.map((month) => ({
      queryKey: ['contentLogs', month, dateBasis],
      queryFn: () => getMonthlyContentLogs(month, timezone, isCalendarEvent, true),
      enabled: hasDropdownTable && isContentMonthlyQueryEnabled && month === currMonth,
      refetchInterval: getMonthlyRefetchInterval(isContentMonthlyQueryEnabled),
      staleTime: CALENDAR_MONTH_STALE_TIME_MS,
    })),
  });

  useEffect(() => {
    if (hasActiveFilters) {
      return;
    }

    const prefetchMonthlyData = async () => {
      const prefetches = getMonthlyPrefetches({
        queryClient,
        dateBasis,
        months: adjacentCalendarMonths,
        configs: [
          {
            enabled: isServiceMonthlyQueryEnabled,
            queryKeyPrefix: 'services',
            queryFn: (month) => getMonthlyServices(month, timezone, isCalendarEvent, true),
          },
          {
            enabled: isMediaMonthlyQueryEnabled,
            queryKeyPrefix: 'media',
            queryFn: (month) => getMonthlyInteractions(month, timezone, isCalendarEvent, true),
          },
          {
            enabled: isEngagementMonthlyQueryEnabled,
            queryKeyPrefix: 'engagements',
            queryFn: (month) => getMonthlyEngagements(month, timezone, isCalendarEvent, true),
          },
          {
            enabled: isContentMonthlyQueryEnabled,
            queryKeyPrefix: 'contentLogs',
            queryFn: (month) => getMonthlyContentLogs(month, timezone, isCalendarEvent, true),
          },
        ],
      });

      await Promise.all(prefetches);
    };

    void prefetchMonthlyData();
  }, [
    adjacentCalendarMonths,
    dateBasis,
    hasActiveFilters,
    isCalendarEvent,
    isContentMonthlyQueryEnabled,
    isEngagementMonthlyQueryEnabled,
    isMediaMonthlyQueryEnabled,
    isServiceMonthlyQueryEnabled,
    queryClient,
    timezone,
  ]);

  const serviceMonthlyData = flattenMonthlyQueryData<FlatService>(serviceMonthlyQueries);
  const mediaMonthlyData = flattenMonthlyQueryData<FlatIssue>(mediaMonthlyQueries);
  const engagementMonthlyData = flattenMonthlyQueryData<FlatEngagement>(engagementMonthlyQueries);
  const contentMonthlyData = flattenMonthlyQueryData<FlatContentLog>(contentMonthlyQueries);
  const hasLoadedServiceWindow = serviceMonthlyQueries.some((query) => query.isSuccess);
  const hasLoadedMediaWindow = mediaMonthlyQueries.some((query) => query.isSuccess);
  const hasLoadedEngagementWindow = engagementMonthlyQueries.some((query) => query.isSuccess);
  const hasLoadedContentWindow = contentMonthlyQueries.some((query) => query.isSuccess);

  const serviceFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.SERVICE);
  }, [filtersOn, filters]);

  const mediaFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.MEDIA_INTERACTION);
  }, [filtersOn, filters]);
  const engagementFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.ENGAGEMENT);
  }, [filtersOn, filters]);
  const contentFiltersQueryString = useMemo(() => {
    if (!filtersOn) return '';
    return getTrackerFilterQueryFromFilters(filters, TrackerDataType.CONTENT_LOG);
  }, [filtersOn, filters]);

  const filteredServiceQuery = useQuery({
    queryKey: ['calendarFilteredServices', serviceFiltersQueryString, dateBasis],
    queryFn: async () => {
      const data = await getCalendarFilteredServices(serviceFiltersQueryString, currMonth, timezone, isCalendarEvent);
      return data || [];
    },
    enabled: hasDropdownTable && isFilteredQueryEnabled(filtersOn, serviceFiltersQueryString, hasServiceSelected),
  });

  const filteredMediaQuery = useQuery({
    queryKey: ['calendarFilteredMedia', mediaFiltersQueryString, dateBasis],
    queryFn: async () => {
      const data = await getCalendarFilteredInteractions(mediaFiltersQueryString, currMonth, timezone, isCalendarEvent);
      return data || [];
    },
    enabled: hasDropdownTable && isFilteredQueryEnabled(filtersOn, mediaFiltersQueryString, hasMediaSelected),
  });
  const filteredEngagementQuery = useQuery({
    queryKey: ['calendarFilteredEngagements', engagementFiltersQueryString, dateBasis],
    queryFn: async () => {
      const data = await getCalendarFilteredEngagements(
        engagementFiltersQueryString,
        currMonth,
        timezone,
        isCalendarEvent
      );
      return data || [];
    },
    enabled: hasDropdownTable && isFilteredQueryEnabled(filtersOn, engagementFiltersQueryString, hasEngagementSelected),
  });
  const filteredContentQuery = useQuery({
    queryKey: ['calendarFilteredContentLogs', contentFiltersQueryString, dateBasis],
    queryFn: async () => {
      const data = await getCalendarFilteredContentLogs(
        contentFiltersQueryString,
        currMonth,
        timezone,
        isCalendarEvent
      );
      return data || [];
    },
    enabled: hasDropdownTable && isFilteredQueryEnabled(filtersOn, contentFiltersQueryString, hasContentSelected),
  });

  const { results: searchResults, isLoading: isSearchLoading } = useCalendarSearchAutocomplete({
    searchInputValue,
    searchCategory,
    selectedViewSet,
    dropdownTable,
  });

  const isFilteredMediaLoading = isFilteredQueryLoading(hasMediaSelected, filtersOn, filteredMediaQuery);
  const isFilteredServiceLoading = isFilteredQueryLoading(hasServiceSelected, filtersOn, filteredServiceQuery);
  const isFilteredEngagementLoading = isFilteredQueryLoading(hasEngagementSelected, filtersOn, filteredEngagementQuery);
  const isFilteredContentLoading = isFilteredQueryLoading(hasContentSelected, filtersOn, filteredContentQuery);
  const currentServiceMonthlyQuery = serviceMonthlyQueries[currentMonthQueryIndex];
  const currentMediaMonthlyQuery = mediaMonthlyQueries[currentMonthQueryIndex];
  const currentEngagementMonthlyQuery = engagementMonthlyQueries[currentMonthQueryIndex];
  const currentContentMonthlyQuery = contentMonthlyQueries[currentMonthQueryIndex];
  const isMonthlyServiceLoading = isMonthlyQueryLoading(
    currentServiceMonthlyQuery,
    hasServiceSelected,
    hasActiveFilters
  );
  const isMonthlyMediaLoading = isMonthlyQueryLoading(currentMediaMonthlyQuery, hasMediaSelected, hasActiveFilters);
  const isMonthlyEngagementLoading = isMonthlyQueryLoading(
    currentEngagementMonthlyQuery,
    hasEngagementSelected,
    hasActiveFilters
  );
  const isMonthlyContentLoading = isMonthlyQueryLoading(
    currentContentMonthlyQuery,
    hasContentSelected,
    hasActiveFilters
  );
  const isCalendarLoading =
    isFilteredMediaLoading ||
    isFilteredServiceLoading ||
    isFilteredEngagementLoading ||
    isFilteredContentLoading ||
    isMonthlyServiceLoading ||
    isMonthlyMediaLoading ||
    isMonthlyEngagementLoading ||
    isMonthlyContentLoading;
  const isInProgressOnly = isCurrentUserInProgressFilterActive(filters, currentUser.userInfo.id);

  const formattedEvents = useMemo(() => {
    if (!dropdownTable) {
      return [];
    }

    if (isFilteredMediaLoading || isFilteredServiceLoading || isFilteredEngagementLoading || isFilteredContentLoading) {
      return [];
    }

    const nextFormattedEvents = [
      ...formatCalendarEvents(
        getCalendarSourceData({
          filteredData: filteredMediaQuery.data,
          filtersOn,
          hasLoadedWindow: hasLoadedMediaWindow,
          isSelected: hasMediaSelected,
          monthlyData: mediaMonthlyData,
        }),
        dropdownTable,
        dateBasis
      ),
      ...formatCalendarEvents(
        getCalendarSourceData({
          filteredData: filteredServiceQuery.data,
          filtersOn,
          hasLoadedWindow: hasLoadedServiceWindow,
          isSelected: hasServiceSelected,
          monthlyData: serviceMonthlyData,
        }),
        dropdownTable,
        dateBasis
      ),
      ...formatCalendarEvents(
        getCalendarSourceData({
          filteredData: filteredEngagementQuery.data,
          filtersOn,
          hasLoadedWindow: hasLoadedEngagementWindow,
          isSelected: hasEngagementSelected,
          monthlyData: engagementMonthlyData,
        }),
        dropdownTable,
        dateBasis
      ),
      ...formatCalendarEvents(
        getCalendarSourceData({
          filteredData: filteredContentQuery.data,
          filtersOn,
          hasLoadedWindow: hasLoadedContentWindow,
          isSelected: hasContentSelected,
          monthlyData: contentMonthlyData,
        }),
        dropdownTable,
        dateBasis
      ),
    ];

    const monthBoundedEvents = filtersOn
      ? nextFormattedEvents.filter((event) => {
          const eventDate = dayjs(event.calendarDate);
          return (
            (eventDate.isAfter(calendarWindowStart) || eventDate.isSame(calendarWindowStart, 'day')) &&
            (eventDate.isBefore(calendarWindowEnd) || eventDate.isSame(calendarWindowEnd, 'day'))
          );
        })
      : nextFormattedEvents;

    const filteredEvents = isInProgressOnly
      ? monthBoundedEvents.filter(
          (event) =>
            event.status === ServiceOrIssueStatus.IN_PROGRESS && event.teamMemberIds.includes(currentUser.userInfo.id)
        )
      : monthBoundedEvents;

    return sortCalendarEventsById(filteredEvents);
  }, [
    contentMonthlyData,
    calendarWindowEnd,
    calendarWindowStart,
    dateBasis,
    dropdownTable,
    engagementMonthlyData,
    filteredContentQuery.data,
    filteredEngagementQuery.data,
    filteredMediaQuery.data,
    filteredServiceQuery.data,
    filtersOn,
    hasContentSelected,
    hasLoadedContentWindow,
    hasEngagementSelected,
    hasLoadedEngagementWindow,
    hasLoadedMediaWindow,
    hasLoadedServiceWindow,
    hasMediaSelected,
    hasServiceSelected,
    isFilteredContentLoading,
    isFilteredEngagementLoading,
    isFilteredMediaLoading,
    isFilteredServiceLoading,
    isInProgressOnly,
    mediaMonthlyData,
    serviceMonthlyData,
    currentUser.userInfo.id,
  ]);

  const eventsByDay = useMemo(() => groupEventsByDay(formattedEvents), [formattedEvents]);
  const selectedDayKey = useMemo(() => dayjs(selectedDate).format('YYYY-MM-DD'), [selectedDate]);
  const selectedDayEvents = eventsByDay[selectedDayKey] ?? [];
  const filterConfig = dropdownTable ? getFilterConfig(actionView, dropdownTable, getEventHandler, setFiltersOn) : null;

  const handleToggleInProgressFilters = useCallback(() => {
    const nextFilters = isCurrentUserInProgressFilterActive(filters, currentUser.userInfo.id)
      ? removeCurrentUserInProgressFilters(filters, currentUser.userInfo.id)
      : getCurrentUserInProgressFilters(filters, dropdownTable?.lead, currentUser.userInfo);

    filtersDispatch({ type: 'setFilters', payload: nextFilters });
    setFiltersOn(Object.keys(nextFilters).length > 0);
  }, [currentUser.userInfo, dropdownTable?.lead, filters, filtersDispatch]);

  const scrollToSelectedDayPanel = useCallback(() => {
    window.requestAnimationFrame(() => {
      selectedDayPanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }, []);

  const handleSelectDate = useCallback((date: Date) => {
    setSelectedDate(dayjs(date).startOf('day').toDate());
  }, []);

  const handleSelectMore = useCallback(
    (date: Date) => {
      handleSelectDate(date);
      scrollToSelectedDayPanel();
    },
    [handleSelectDate, scrollToSelectedDayPanel]
  );

  if (!dropdownTable) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          paddingLeft: 'clamp(16px, 2.6vw, 34px)',
          paddingRight: 'clamp(16px, 2.6vw, 34px)',
          paddingTop: 'clamp(10px, 1.6vw, 18px)',
          paddingBottom: 'clamp(10px, 1.4vw, 14px)',
        }}
      >
        <div className="spinner-container">
          <Spinner animation="border" />
        </div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingLeft: 'clamp(16px, 2.6vw, 34px)',
        paddingRight: 'clamp(16px, 2.6vw, 34px)',
        paddingTop: 'clamp(10px, 1.6vw, 18px)',
        paddingBottom: 'clamp(10px, 1.4vw, 14px)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          minHeight: 0,
        }}
      >
        <CalendarTopBar
          currMonth={currMonth}
          searchCategoryOptions={calendarSearchCategoryOptions}
          searchCategory={searchCategory}
          searchValue={searchInputValue}
          searchResults={searchResults}
          isSearchLoading={isSearchLoading}
          areFiltersDisabled={false}
          isInProgressOnly={isInProgressOnly}
          isInProgressDisabled={false}
          dateBasis={dateBasis}
          onSearchCategoryChange={setSearchCategory}
          onSearchValueChange={setSearchInputValue}
          onSearchClear={() => setSearchInputValue('')}
          onResultSelect={openSearchResultModal}
          onToggleInProgress={handleToggleInProgressFilters}
          onDateBasisChange={setDateBasis}
          showActions={hasCalendarActions}
          filterConfig={filterConfig}
          onMonthChange={handleMonthChange}
          onNewEntryClick={(event) => handleNewEntryClick(event, dayjs().startOf('day').toDate())}
        />
        <Box
          sx={{
            width: '100%',
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              px: 1,
              pt: 1,
              pb: 2,
            }}
            aria-busy={isCalendarLoading}
          >
            {isCalendarLoading ? (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  px: 3,
                  pt: 6,
                  backgroundColor: 'rgba(251, 252, 254, 0.72)',
                  backdropFilter: 'blur(2px)',
                  borderRadius: '20px',
                }}
              >
                <Box
                  sx={{
                    minWidth: 220,
                    px: 3,
                    py: 2.25,
                    borderRadius: '18px',
                    textAlign: 'center',
                    backgroundColor: calendarUiPalette.selectedDayPanel.entryCardBackground,
                    border: `1px solid ${calendarUiPalette.selectedDayPanel.entryCardBorder}`,
                    boxShadow: calendarUiPalette.selectedDayPanel.entryCardShadow,
                  }}
                >
                  <CircularProgress size={28} sx={{ color: theme.palette.primary.main }} />
                  <Typography
                    sx={{
                      mt: 1.5,
                      color: calendarUiPalette.selectedDayPanel.title,
                      fontWeight: theme.typography.fontWeightBold,
                    }}
                  >
                    Loading calendar
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      color: calendarUiPalette.selectedDayPanel.summaryText,
                      ...theme.typography.body2,
                    }}
                  >
                    Pulling entries for this view.
                  </Typography>
                </Box>
              </Box>
            ) : null}
            <CalendarMonthGrid
              currMonth={currMonth}
              selectedDate={selectedDate}
              eventsByDay={eventsByDay}
              headerContent={
                <CalendarCategoryBar
                  categories={availableCategoryOptions}
                  selectedViews={selectedViews}
                  onToggleView={handleToggleView}
                />
              }
              onSelectDate={handleSelectDate}
              onSelectMore={handleSelectMore}
              onSelectEvent={openEventModal}
              onDoubleClickEvent={openEventModal}
            />
            <CalendarSelectedDayPanel
              panelRef={selectedDayPanelRef}
              dateBasis={dateBasis}
              selectedDate={selectedDate}
              events={selectedDayEvents}
              onSelectEvent={openEventModal}
            />
            {isEventModalOpen && <EventModal event={selectedEvent} onClose={closeEventModal} />}
            <CalendarNewEntryMenu
              anchorEl={newEntryMenuAnchor}
              categories={addableCategories}
              onClose={closeNewEntryMenu}
              onSelectCategory={openNewEntryDrawer}
            />
            {newEntryView && (
              <QueryAdderDrawer openForm setOpenForm={closeNewEntryDrawer}>
                <QueryAdder
                  title={getEventHandler(newEntryView).newItemModalTitle}
                  dropdownTable={dropdownTable}
                  currMonth={currMonth}
                  setOpenForm={closeNewEntryDrawer}
                  eventHandler={getEventHandler(newEntryView)}
                  initialEntryDate={newEntryInitialDate}
                />
              </QueryAdderDrawer>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CalendarView;
